<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import ExternalHtmlWrapper from '@/components/_core/ExternalHtmlWrapper.vue'
import SolutionName from '@/components/solutions/SolutionName.vue'
import EVENT_STATUS from '@/constants/eventStatus'

type EventStatusTypes = (typeof EVENT_STATUS)[keyof typeof EVENT_STATUS]

export default defineComponent({
  name: 'EventIntro',

  components: {
    ExternalHtmlWrapper,
    SolutionName,
  },

  props: {
    item: {
      type: Object as PropType<SolutionEventItem>,
      required: true,
    },

    eventStatus: {
      type: String as PropType<EventStatusTypes>,
      required: true,
    },
  },

  setup(props) {
    const statusLabel = computed(() => {
      switch (props.eventStatus) {
        case EVENT_STATUS.ENDED:
          return '本イベントは終了しました'
        case EVENT_STATUS.ENTRY_NOT_STARTED:
          return '申し込み受付は開始されていません。'
        case EVENT_STATUS.ENTRY_CLOSED:
          return '申し込み受付は終了しました。'
        case EVENT_STATUS.WAITLIST:
          return '受付待機（キャンセル待ち）で受付中'
        default:
          return ''
      }
    })

    return {
      // computed
      statusLabel,
    }
  },
})
</script>

<template>
  <div class="l-unit__eventIntroduction">
    <div class="l-unit__eventIntroduction--header">
      <h1 class="h1 u-text__xx-huge u-text__bold">
        {{ item.title }}
      </h1>
    <!-- /.l-unit__eventIntroduction header --></div>

    <div
      v-if="item.thumbImageUrl"
      class="l-unit__eventIntroduction--image">
      <img
        class="img"
        :src="item.thumbImageUrl"
        fetchpriority="high"
        alt="">
    <!-- /.l-unit__eventIntroduction image --></div>

    <div
      v-if="item.description"
      class="l-unit__eventIntroduction--description is-summary">
      <div class="o-label__description">
        <p class="o-label__description--item">
          {{ item.description }}
        </p>
      <!-- /.o-label__description --></div>
    <!-- /.l-unit__eventIntroduction description --></div>

    <ExternalHtmlWrapper
      v-if="item.introduction"
      :class-names="[
        'l-unit__eventIntroduction--description',
        'is-introduction',
      ]"
      :contents-html="item.introduction" />

    <div
      v-if="$slots.additional"
      class="l-unit__eventIntroduction--description is-additional">
      <slot name="additional" />
    <!-- /.l-unit__eventIntroduction description --></div>

    <div
      v-if="item.solution"
      class="l-unit__eventIntroduction--footer is-name">
      <SolutionName
        :solution="item.solution"
        :is-truncate="false"
        has-link
        size="normal" />
    <!-- /.l-unit__eventIntroduction footer --></div>

    <div
      v-if="statusLabel"
      class="l-unit__eventIntroduction--footer is-status">
      <div class="o-label__status">
        <p class="o-label__status--item">
          {{ statusLabel }}
        </p>
      <!-- /.o-label__status --></div>
    <!-- /.l-unit__eventIntroduction footer --></div>
  <!-- /.l-unit__eventIntroduction --></div>
</template>

<style lang="sass" scoped>
// layout
.l-unit__eventIntroduction
  $root: &
  display: flex
  flex-direction: column

  & > *
    width: 100%

  & &--header
    width: 1px
    height: 1px
    position: absolute
    overflow: hidden
    clip: rect(0, 0, 0, 0)
    white-space: nowrap
    margin: -1px
    padding: 0
    border: 0

  & &--image
    order: 1
    margin-top: 1em

    > .img
      height: 100%
      display: block
      object-fit: contain

  & &--description
    margin-top: 1em

    &.is-introduction,
    &.is-additional
      order: 2

  & &--footer
    &.is-name
      order: -1
      margin-top: 1em

    &.is-status
      order: -1
      margin-top: 1em

// object
.o-label__description
  & &--item
    color: $font-light-gray
    font-size: $font-small

.o-label__status
  display: flex
  justify-content: flex-start

  & &--item
    color: $font-white
    font-size: $font-small
    text-align: center
    padding: changeSizePx2Em(8) changeSizePx2Em(16)
    background-color: rgba($bg-dark-orange, 1)
    border-radius: $border-radius
</style>
